import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { LayoutDashboard, Ticket, Users, Calendar, LogOut, Wrench, Settings as SettingsIcon, UsersRound, BarChart3, MessageSquare, ScrollText } from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testId, badge }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
        isActive
          ? "bg-blue-50 text-blue-700"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <Icon className="w-4 h-4" />
    <span>{label}</span>
    {badge > 0 && (
      <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-semibold flex items-center justify-center" data-testid="nav-unread-badge">
        {badge > 99 ? "99+" : badge}
      </span>
    )}
  </NavLink>
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/conversations/unread-count");
        if (active) setUnread(data?.unread || 0);
      } catch (e) { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 15000);
    const onUpdate = () => poll();
    window.addEventListener("conversations-updated", onUpdate);
    return () => { active = false; clearInterval(t); window.removeEventListener("conversations-updated", onUpdate); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-slate-200 bg-white">
        <div className="h-16 flex items-center px-5 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-home-link">
            <div className="w-8 h-8 rounded-md bg-blue-700 flex items-center justify-center text-white">
              <Wrench className="w-4 h-4" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-base text-slate-900">FixTrack</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Repair Tickets</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavItem to="/" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />
          <NavItem to="/tickets" icon={Ticket} label="Tickets" testId="nav-tickets" />
          <NavItem to="/customers" icon={Users} label="Customers" testId="nav-customers" />
          <NavItem to="/schedule" icon={Calendar} label="Schedule" testId="nav-schedule" />
          <NavItem to="/messages" icon={MessageSquare} label="Messages" testId="nav-messages" badge={unread} />
          <NavItem to="/message-log" icon={ScrollText} label="Message log" testId="nav-message-log" />
          {user?.role === "admin" && (
            <NavItem to="/analytics" icon={BarChart3} label="Analytics" testId="nav-analytics" />
          )}
          {user?.role === "admin" && (
            <NavItem to="/settings" icon={SettingsIcon} label="Settings" testId="nav-settings" />
          )}
          {user?.role === "admin" && (
            <NavItem to="/settings/team" icon={UsersRound} label="Team" testId="nav-team" />
          )}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="px-2 py-2 text-xs text-slate-500">
            Signed in as
            <div className="text-sm text-slate-900 font-medium truncate" data-testid="user-email">{user?.email}</div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 h-14 border-b border-slate-200 bg-white px-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-home-link-mobile">
          <div className="w-7 h-7 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-4 h-4" />
          </div>
          <span className="font-heading font-bold">FixTrack</span>
        </Link>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => navigate("/message-log")} data-testid="msglog-btn-mobile">
            <ScrollText className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="logout-btn-mobile">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 md:ml-0 pt-14 md:pt-0">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
        {/* Mobile bottom nav */}
        <nav className={`md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 grid ${user?.role === "admin" ? "grid-cols-6" : "grid-cols-5"}`}>
          <NavLink to="/" end className={({ isActive }) => `py-2 flex flex-col items-center text-xs ${isActive ? "text-blue-700" : "text-slate-600"}`} data-testid="nav-dashboard-m">
            <LayoutDashboard className="w-4 h-4 mb-0.5" /> Home
          </NavLink>
          <NavLink to="/tickets" className={({ isActive }) => `py-2 flex flex-col items-center text-xs ${isActive ? "text-blue-700" : "text-slate-600"}`} data-testid="nav-tickets-m">
            <Ticket className="w-4 h-4 mb-0.5" /> Tickets
          </NavLink>
          <NavLink to="/customers" className={({ isActive }) => `py-2 flex flex-col items-center text-xs ${isActive ? "text-blue-700" : "text-slate-600"}`} data-testid="nav-customers-m">
            <Users className="w-4 h-4 mb-0.5" /> People
          </NavLink>
          <NavLink to="/messages" className={({ isActive }) => `relative py-2 flex flex-col items-center text-xs ${isActive ? "text-blue-700" : "text-slate-600"}`} data-testid="nav-messages-m">
            <MessageSquare className="w-4 h-4 mb-0.5" /> Chat
            {unread > 0 && (
              <span className="absolute top-1 right-[22%] min-w-[16px] h-4 px-1 rounded-full bg-blue-600 text-white text-[9px] font-semibold flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </NavLink>
          <NavLink to="/schedule" className={({ isActive }) => `py-2 flex flex-col items-center text-xs ${isActive ? "text-blue-700" : "text-slate-600"}`} data-testid="nav-schedule-m">
            <Calendar className="w-4 h-4 mb-0.5" /> Schedule
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/analytics" className={({ isActive }) => `py-2 flex flex-col items-center text-xs ${isActive ? "text-blue-700" : "text-slate-600"}`} data-testid="nav-analytics-m">
              <BarChart3 className="w-4 h-4 mb-0.5" /> KPIs
            </NavLink>
          )}
        </nav>
        <div className="md:hidden h-14" />
      </main>
    </div>
  );
}
